/**
 * Username ownership claim — Ed25519-signed identity binding.
 *
 * Mirrors `kaether_for_ios/Stoat/E2EE/UsernameClaim.swift` exactly.
 *
 * A claim signs `(schema, canonicalName, discriminator, identityPublicKey,
 * claimedAt)` under the claimer's Ed25519 identity private key. The server
 * stores the signed claim; any client that fetches the username reproduces
 * the canonical bytes and verifies the signature against the embedded
 * identity public key. This blocks a hostile server from reassigning
 * `alice#0000` to a different identity — the server has no way to mint a
 * valid signature without that identity's private key.
 *
 * ## Wire format of the encodable string
 *
 *   kaether:user:claim:v1:<base64url(JSON({ payload, signature }))>
 *
 * ## JSON shape
 *
 *   { "payload": { "schema": 1,
 *                  "username": { "canonicalName": "alice", "discriminator": 1 },
 *                  "identityPublicKey": "<base64std-of-32-bytes>",
 *                  "claimedAt": 1700000000 },
 *     "signature": "<base64std-of-64-bytes>" }
 *
 * `signature` is Ed25519 over the *canonical bytes* defined below — NOT
 * over the JSON. JSON field ordering and whitespace cannot vary the signed
 * surface.
 *
 * ## Canonical signed surface (matches iOS byte-for-byte)
 *
 *   schema (8 bytes, little-endian UInt64)
 *   canonicalName (UTF-8)
 *   0x1F
 *   discriminator (2 bytes, little-endian UInt16)
 *   0x1F
 *   identityPublicKey (32 bytes)
 *   0x1F
 *   claimedAt (8 bytes, little-endian Int64, bit-pattern)
 *
 * The 0x1F separators prevent splice attacks between adjacent
 * variable-length fields.
 *
 * Pure logic / SubtleCrypto only. No SolidJS imports. No network calls.
 */

import type { Username } from "./Username";

// ─── value types ─────────────────────────────────────────────────────────────

export interface UsernameClaimPayload {
  readonly schema: number;
  readonly username: Username;
  /** 32-byte Ed25519 public key as Uint8Array. */
  readonly identityPublicKey: Uint8Array;
  /** Unix timestamp (seconds). */
  readonly claimedAt: bigint;
}

export interface UsernameClaim {
  readonly payload: UsernameClaimPayload;
  /** 64-byte Ed25519 signature over `canonicalBytes(payload)`. */
  readonly signature: Uint8Array;
}

export const CURRENT_SCHEMA = 1;

// ─── error types ─────────────────────────────────────────────────────────────

export type UsernameClaimErrorKind =
  | { kind: "unsupportedSchema"; schema: number }
  | { kind: "invalidSignature" }
  | { kind: "malformed"; reason: string }
  | { kind: "invalidIdentityKeyLength"; length: number }
  | { kind: "invalidSignatureLength"; length: number };

export class UsernameClaimError extends Error {
  readonly detail: UsernameClaimErrorKind;
  constructor(detail: UsernameClaimErrorKind) {
    super(`[UsernameClaim] ${detail.kind}`);
    this.name = "UsernameClaimError";
    this.detail = detail;
  }
}

// ─── wire constants ───────────────────────────────────────────────────────────

export const CLAIM_PREFIX = "kaether:user:claim:v1:";

/** 0x1F unit separator byte separating variable-length fields. */
const SEP = 0x1f;
const ED25519_PUB_BYTES = 32;
const ED25519_SIG_BYTES = 64;

/**
 * PKCS#8 prefix wrapping a 32-byte Ed25519 seed. Chrome's W3C WebCrypto refuses
 * `importKey("raw", ..., ["sign"])` for Ed25519 — only public keys can be
 * imported as `"raw"`. Private 32-byte seeds must arrive as `"pkcs8"`.
 * This 16-byte header is the constant ASN.1 prefix for an Ed25519
 * OneAsymmetricKey containing a 32-byte CurvePrivateKey OCTET STRING
 * (RFC 8410 §7). Total wrapped length = 48 bytes.
 *
 * Same constant used in `sender-keys/SenderKey.ts::ED25519_PKCS8_PREFIX` and
 * `account-rotation/AccountIdentifierRotation.ts`.
 */
const ED25519_PKCS8_PREFIX = new Uint8Array([
  0x30, 0x2e, 0x02, 0x01, 0x00, 0x30, 0x05, 0x06,
  0x03, 0x2b, 0x65, 0x70, 0x04, 0x22, 0x04, 0x20,
]);

/** Wrap a raw 32-byte Ed25519 seed in PKCS#8 DER. */
function wrapEd25519Pkcs8(seed: Uint8Array): Uint8Array {
  const out = new Uint8Array(ED25519_PKCS8_PREFIX.length + seed.length);
  out.set(ED25519_PKCS8_PREFIX, 0);
  out.set(seed, ED25519_PKCS8_PREFIX.length);
  return out;
}

// ─── canonical bytes ──────────────────────────────────────────────────────────

/**
 * Produce the canonical bytes that are signed / verified.
 *
 * Layout (little-endian multibyte integers):
 *   schema(8 LE) | canonicalName(UTF-8) | 0x1F | discriminator(2 LE) | 0x1F
 *   | identityPublicKey(32) | 0x1F | claimedAt(8 LE)
 *
 * Web port of `UsernameClaimCodec.canonicalBytes(_:)` in UsernameClaim.swift.
 */
export function claimCanonicalBytes(p: UsernameClaimPayload): Uint8Array {
  const enc = new TextEncoder();
  const nameBytes = enc.encode(p.username.canonicalName);

  // schema: 8 bytes LE UInt64
  const schemaBuf = new ArrayBuffer(8);
  new DataView(schemaBuf).setBigUint64(0, BigInt(p.schema), /*LE*/ true);

  // discriminator: 2 bytes LE UInt16
  const discBuf = new ArrayBuffer(2);
  new DataView(discBuf).setUint16(0, p.username.discriminator, /*LE*/ true);

  // claimedAt: 8 bytes LE Int64 (bit-pattern, matching Swift's UInt64(bitPattern:))
  const tsBuf = new ArrayBuffer(8);
  // bigint can be negative for pre-epoch timestamps — use bit-pattern cast.
  new DataView(tsBuf).setBigInt64(0, p.claimedAt, /*LE*/ true);

  // Total length
  const totalLen =
    8 + // schema
    nameBytes.length +
    1 + // 0x1F
    2 + // discriminator
    1 + // 0x1F
    ED25519_PUB_BYTES +
    1 + // 0x1F
    8; // claimedAt

  const out = new Uint8Array(totalLen);
  let pos = 0;

  out.set(new Uint8Array(schemaBuf), pos); pos += 8;
  out.set(nameBytes, pos); pos += nameBytes.length;
  out[pos++] = SEP;
  out.set(new Uint8Array(discBuf), pos); pos += 2;
  out[pos++] = SEP;
  out.set(p.identityPublicKey, pos); pos += ED25519_PUB_BYTES;
  out[pos++] = SEP;
  out.set(new Uint8Array(tsBuf), pos);

  return out;
}

// ─── sign / verify ────────────────────────────────────────────────────────────

/**
 * Sign a `UsernameClaimPayload` with the caller's Ed25519 identity private key.
 *
 * `identityPrivateKey` must be the raw 32-byte Ed25519 seed (matches iOS
 * `Curve25519.Signing.PrivateKey.rawRepresentation`).
 *
 * Web port of `UsernameClaimCodec.sign(payload:identityPrivateKey:)`.
 */
export async function signClaim(
  payload: UsernameClaimPayload,
  identityPrivateKey: Uint8Array,
): Promise<UsernameClaim> {
  if (payload.identityPublicKey.length !== ED25519_PUB_BYTES) {
    throw new UsernameClaimError({
      kind: "invalidIdentityKeyLength",
      length: payload.identityPublicKey.length,
    });
  }

  const canonical = claimCanonicalBytes(payload);

  let privKey: CryptoKey;
  try {
    // Chrome's W3C Ed25519 implementation refuses `importKey("raw", ...)` for
    // private keys; the 32-byte seed must be wrapped in PKCS#8 first.
    privKey = await crypto.subtle.importKey(
      "pkcs8",
      wrapEd25519Pkcs8(identityPrivateKey),
      { name: "Ed25519" },
      false,
      ["sign"],
    );
  } catch (err) {
    throw new UsernameClaimError({
      kind: "malformed",
      reason: `private key import failed: ${String(err)}`,
    });
  }

  const sigBuf = await crypto.subtle.sign({ name: "Ed25519" }, privKey, canonical);
  return { payload, signature: new Uint8Array(sigBuf) };
}

/**
 * Verify the Ed25519 signature inside a claim against its embedded
 * `identityPublicKey`. Returns `true` if valid; returns `false` on any
 * structural problem so callers can fail-closed.
 *
 * Web port of `UsernameClaimCodec.verify(_:)`.
 */
export async function verifyClaim(claim: UsernameClaim): Promise<boolean> {
  if (claim.signature.length !== ED25519_SIG_BYTES) return false;
  if (claim.payload.identityPublicKey.length !== ED25519_PUB_BYTES) return false;

  let pubKey: CryptoKey;
  try {
    pubKey = await crypto.subtle.importKey(
      "raw",
      claim.payload.identityPublicKey,
      { name: "Ed25519" },
      false,
      ["verify"],
    );
  } catch {
    return false;
  }

  try {
    return await crypto.subtle.verify(
      { name: "Ed25519" },
      pubKey,
      claim.signature,
      claimCanonicalBytes(claim.payload),
    );
  } catch {
    return false;
  }
}

// ─── encode / decode ──────────────────────────────────────────────────────────

/** Cheap prefix check — does not validate payload or signature. */
export function isClaim(s: string): boolean {
  return s.startsWith(CLAIM_PREFIX);
}

/** URL-safe base64 encode (no padding). */
function base64UrlEncode(data: Uint8Array): string {
  // Use btoa on Latin-1 bytes.
  let binary = "";
  for (let i = 0; i < data.length; i++) {
    binary += String.fromCharCode(data[i]);
  }
  return btoa(binary)
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=/g, "");
}

/** URL-safe base64 decode. Returns null on invalid input. */
function base64UrlDecode(s: string): Uint8Array | null {
  try {
    let b64 = s.replace(/-/g, "+").replace(/_/g, "/");
    const pad = (4 - (b64.length % 4)) % 4;
    b64 += "=".repeat(pad);
    const binary = atob(b64);
    const out = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      out[i] = binary.charCodeAt(i);
    }
    return out;
  } catch {
    return null;
  }
}

/** JSON wire shape (all binary fields as standard base64 strings). */
interface ClaimWireJson {
  payload: {
    schema: number;
    username: { canonicalName: string; discriminator: number };
    identityPublicKey: string; // base64std
    claimedAt: number; // JSON number (safe for timestamps up to 2^53)
  };
  signature: string; // base64std
}

function uint8ToBase64(bytes: Uint8Array): string {
  let binary = "";
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

function base64ToUint8(s: string): Uint8Array | null {
  try {
    const binary = atob(s);
    const out = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      out[i] = binary.charCodeAt(i);
    }
    return out;
  } catch {
    return null;
  }
}

/**
 * Encode a `UsernameClaim` to the wire string
 * `kaether:user:claim:v1:<base64url(JSON)>`.
 *
 * Web port of `UsernameClaimCodec.encode(_:)`.
 */
export function encodeClaim(claim: UsernameClaim): string {
  const wire: ClaimWireJson = {
    payload: {
      schema: claim.payload.schema,
      username: {
        canonicalName: claim.payload.username.canonicalName,
        discriminator: claim.payload.username.discriminator,
      },
      identityPublicKey: uint8ToBase64(claim.payload.identityPublicKey),
      claimedAt: Number(claim.payload.claimedAt),
    },
    signature: uint8ToBase64(claim.signature),
  };
  const json = JSON.stringify(wire);
  const jsonBytes = new TextEncoder().encode(json);
  return CLAIM_PREFIX + base64UrlEncode(jsonBytes);
}

/**
 * Parse, validate schema + lengths, and verify the Ed25519 signature.
 * Throws `UsernameClaimError` if anything is wrong.
 *
 * Web port of `UsernameClaimCodec.decode(_:)`.
 */
export async function decodeClaim(s: string): Promise<UsernameClaim> {
  if (!isClaim(s)) {
    throw new UsernameClaimError({
      kind: "malformed",
      reason: "not a username claim",
    });
  }
  const b64 = s.slice(CLAIM_PREFIX.length);
  const jsonBytes = base64UrlDecode(b64);
  if (!jsonBytes) {
    throw new UsernameClaimError({
      kind: "malformed",
      reason: "invalid base64url",
    });
  }

  let wire: ClaimWireJson;
  try {
    wire = JSON.parse(new TextDecoder().decode(jsonBytes)) as ClaimWireJson;
  } catch (err) {
    throw new UsernameClaimError({
      kind: "malformed",
      reason: `json decode failed: ${String(err)}`,
    });
  }

  if (typeof wire?.payload?.schema !== "number" ||
      typeof wire?.payload?.username?.canonicalName !== "string" ||
      typeof wire?.payload?.username?.discriminator !== "number" ||
      typeof wire?.payload?.identityPublicKey !== "string" ||
      typeof wire?.payload?.claimedAt !== "number" ||
      typeof wire?.signature !== "string") {
    throw new UsernameClaimError({ kind: "malformed", reason: "missing fields" });
  }

  if (wire.payload.schema !== CURRENT_SCHEMA) {
    throw new UsernameClaimError({
      kind: "unsupportedSchema",
      schema: wire.payload.schema,
    });
  }

  const identityPublicKey = base64ToUint8(wire.payload.identityPublicKey);
  if (!identityPublicKey) {
    throw new UsernameClaimError({
      kind: "malformed",
      reason: "invalid identityPublicKey base64",
    });
  }
  if (identityPublicKey.length !== ED25519_PUB_BYTES) {
    throw new UsernameClaimError({
      kind: "invalidIdentityKeyLength",
      length: identityPublicKey.length,
    });
  }

  const signature = base64ToUint8(wire.signature);
  if (!signature) {
    throw new UsernameClaimError({
      kind: "malformed",
      reason: "invalid signature base64",
    });
  }
  if (signature.length !== ED25519_SIG_BYTES) {
    throw new UsernameClaimError({
      kind: "invalidSignatureLength",
      length: signature.length,
    });
  }

  const claim: UsernameClaim = {
    payload: {
      schema: wire.payload.schema,
      username: {
        canonicalName: wire.payload.username.canonicalName,
        discriminator: wire.payload.username.discriminator,
      },
      identityPublicKey,
      claimedAt: BigInt(wire.payload.claimedAt),
    },
    signature,
  };

  const valid = await verifyClaim(claim);
  if (!valid) {
    throw new UsernameClaimError({ kind: "invalidSignature" });
  }

  return claim;
}

/**
 * Convenience: returns `null` for non-claims. Throws only for malformed
 * payloads that *did* claim to be claims.
 *
 * Web port of `UsernameClaimCodec.tryDecode(_:)`.
 */
export async function tryDecodeClaim(
  s: string,
): Promise<UsernameClaim | null> {
  if (!isClaim(s)) return null;
  return decodeClaim(s);
}
